import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  activateKioskSessionRequest,
  deactivateKioskSessionRequest,
  getCurrentKioskSessionRequest,
} from 'api/requests/kiosk-session-requests';
import { fromResponseToKioskSession } from 'mappers/kiosk-mapper';
import kioskLanguageService from 'services/kiosk-language-service';
import kioskSessionService from 'services/kiosk-session-service';
import { logoutAction } from 'store/actions/auth-actions';
import {
  KIOSK__ACTIVATE,
  KIOSK__DEACTIVATE,
  KIOSK__RESUME,
  KIOSK__SET_ERROR,
  KIOSK__SET_LOADING,
  KIOSK__SET_SESSION,
  KIOSK__SET_STATUS,
} from 'store/constants';
import { preloadAllLanguages } from 'translations';
import { GENERAL__ERROR_MESSAGE } from 'translations/constants';
import { KioskStatus } from 'types/kiosk/KioskStatus';
import { TKioskSession } from 'types/kiosk/TKioskSession';
import { mapKioskError } from 'utils/kiosk-error';

/**
 * Bring the kiosk UI into the session's language. A language picked on this device but not yet saved
 * (offline / failed save) wins over the server's value and is re-sent. All languages are loaded up front
 * so later switches are instant and work offline.
 */
const applyKioskLanguage = (session: TKioskSession | null): void => {
  const pending = kioskSessionService.getPendingLanguage();
  const target = pending ?? session?.language ?? null;
  if (target) {
    kioskLanguageService.apply(target);
  }
  if (pending) {
    kioskLanguageService.settle();
  }
  preloadAllLanguages();
};

export const setKioskStatusAction = createAction<KioskStatus>(KIOSK__SET_STATUS);
export const setKioskSessionAction = createAction<TKioskSession | null>(KIOSK__SET_SESSION);
export const setKioskLoadingAction = createAction<boolean>(KIOSK__SET_LOADING);
export const setKioskErrorAction = createAction<string | null>(KIOSK__SET_ERROR);

export interface ActivateKioskProps {
  locationId: number;
  costCentreId?: number;
  name?: string;
}

/**
 * Activate a kiosk on this device. On success the durable kiosk token is persisted, the gate is
 * raised, and the manager's session is dropped (no redirect — the kiosk gate takes over the shell).
 */
export const activateKioskAction = createAsyncThunk(KIOSK__ACTIVATE, async (props: ActivateKioskProps, thunkApi) => {
  thunkApi.dispatch(setKioskLoadingAction(true));
  thunkApi.dispatch(setKioskErrorAction(null));

  const deviceIdentifier = kioskSessionService.getOrCreateDeviceIdentifier();

  try {
    const response = await activateKioskSessionRequest({
      deviceIdentifier,
      surface: 'Web',
      locationId: props.locationId,
      ...(props.costCentreId ? { costCentreId: props.costCentreId } : {}),
      ...(props.name ? { name: props.name } : {}),
      operatingSystem: kioskSessionService.getOperatingSystem(),
    });

    const session = fromResponseToKioskSession(response, deviceIdentifier);

    if (!response?.kioskToken || !session) {
      thunkApi.dispatch(setKioskErrorAction(GENERAL__ERROR_MESSAGE));
      thunkApi.dispatch(setKioskLoadingAction(false));
      return false;
    }

    // Persist the durable credential first — it is the source of truth across reloads.
    kioskSessionService.persist(response.kioskToken, session);

    // Raise the gate BEFORE dropping the manager session, so the router never flashes the
    // login screen between losing the manager token and the kiosk becoming active.
    // A fresh session starts in the activating manager's language; nothing from a previous session carries over.
    kioskSessionService.clearPendingLanguage();
    applyKioskLanguage(session);
    // Load every language before the kiosk shows, so the first switches are instant and cannot race a download.
    await preloadAllLanguages();

    thunkApi.dispatch(setKioskSessionAction(session));
    thunkApi.dispatch(setKioskStatusAction(KioskStatus.ACTIVE));

    // Drop the manager session (no redirect). Kiosk localStorage keys survive this.
    try {
      await thunkApi.dispatch(logoutAction(false));
    } catch {
      // Swallow — the kiosk gate is already up; a logout hiccup must not surface as an error.
    }

    thunkApi.dispatch(setKioskLoadingAction(false));
    return true;
  } catch (error) {
    thunkApi.dispatch(setKioskErrorAction(mapKioskError(error)));
    thunkApi.dispatch(setKioskLoadingAction(false));
    return false;
  }
});

/**
 * Re-validate a persisted kiosk token on launch.
 * 200 -> active; 401/404 -> clear + inactive; network/5xx -> stay locked on the cached session.
 */
export const resumeKioskAction = createAsyncThunk(KIOSK__RESUME, async (__, thunkApi) => {
  const token = kioskSessionService.getToken();
  if (!token) {
    thunkApi.dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
    return;
  }

  const deviceIdentifier = kioskSessionService.getOrCreateDeviceIdentifier();
  const cached = kioskSessionService.getCachedSession();

  // Lock immediately from the cached session while we validate.
  thunkApi.dispatch(setKioskStatusAction(KioskStatus.RESUMING));
  if (cached) {
    applyKioskLanguage(cached);
    thunkApi.dispatch(setKioskSessionAction(cached));
  }

  try {
    const response = await getCurrentKioskSessionRequest(token);
    const session = fromResponseToKioskSession(response, deviceIdentifier);

    if (!session) {
      kioskSessionService.clear();
      thunkApi.dispatch(setKioskSessionAction(null));
      thunkApi.dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
      return;
    }

    kioskSessionService.persist(token, session);
    applyKioskLanguage(session);
    thunkApi.dispatch(setKioskSessionAction(session));
    thunkApi.dispatch(setKioskStatusAction(KioskStatus.ACTIVE));
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 401 || status === 404) {
      // Session was revoked server-side.
      kioskSessionService.clear();
      thunkApi.dispatch(setKioskSessionAction(null));
      thunkApi.dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
    } else if (cached) {
      // Offline / transient server error: stay locked on the cached session.
      thunkApi.dispatch(setKioskStatusAction(KioskStatus.ACTIVE));
    } else {
      thunkApi.dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
    }
  }
});

/**
 * Deactivate via the manager email + password challenge.
 * Success -> clear + return to sign-in. INVALID_CREDENTIALS -> stay locked, surface the error.
 */
export const deactivateKioskAction = createAsyncThunk(
  KIOSK__DEACTIVATE,
  async (props: { email: string; password: string }, thunkApi) => {
    thunkApi.dispatch(setKioskLoadingAction(true));
    thunkApi.dispatch(setKioskErrorAction(null));

    const token = kioskSessionService.getToken();
    const deviceIdentifier = kioskSessionService.getOrCreateDeviceIdentifier();

    if (!token) {
      // No live token locally — nothing to authenticate against; just exit to sign-in.
      kioskSessionService.clear();
      thunkApi.dispatch(setKioskSessionAction(null));
      thunkApi.dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
      thunkApi.dispatch(setKioskLoadingAction(false));
      window.location.href = '/';
      return true;
    }

    try {
      await deactivateKioskSessionRequest(token, {
        deviceIdentifier,
        email: props.email,
        password: props.password,
      });
      kioskSessionService.clear();
      thunkApi.dispatch(setKioskSessionAction(null));
      thunkApi.dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
      thunkApi.dispatch(setKioskLoadingAction(false));
      window.location.href = '/';
      return true;
    } catch (error) {
      thunkApi.dispatch(setKioskErrorAction(mapKioskError(error)));
      thunkApi.dispatch(setKioskLoadingAction(false));
      return false;
    }
  },
);
